import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useEffect, useMemo, useRef } from 'react'
import { useShouldUseAdminManualTasks } from './useShouldUseAdminManualTasks'

export const useManualTasksByExecutionId = ({
  executionId,
  executionTaskCount
}: {
  executionId: string | null
  executionTaskCount?: number
}) => {
  const shouldUseAdminQuery = useShouldUseAdminManualTasks()

  const adminManualTasksQuery = useQuery(
    dashboardv99990101Client.getAdminManualTasks,
    {
      executionId: executionId ?? '',
      limit: 100
    },
    {
      enabled: Boolean(executionId && shouldUseAdminQuery)
    }
  )

  const assignedManualTasksQuery = useQuery(
    dashboardv99990101Client.getManualTasks,
    {
      executionId: executionId ?? '',
      limit: 100
    },
    {
      enabled: Boolean(executionId && !shouldUseAdminQuery)
    }
  )

  /**
   * When the automation execution gains new tasks (e.g. a review task is
   * created after contract extraction completes), refetch manual tasks so
   * the preview stays in sync without waiting for the next poll interval.
   */
  const prevTaskCountRef = useRef(executionTaskCount)
  useEffect(() => {
    if (
      executionTaskCount !== undefined &&
      prevTaskCountRef.current !== undefined &&
      executionTaskCount > prevTaskCountRef.current
    ) {
      if (shouldUseAdminQuery) {
        adminManualTasksQuery.refetch()
      } else {
        assignedManualTasksQuery.refetch()
      }
    }
    prevTaskCountRef.current = executionTaskCount
  }, [
    executionTaskCount,
    shouldUseAdminQuery,
    adminManualTasksQuery,
    assignedManualTasksQuery
  ])

  const manualTasks = useMemo(() => {
    if (shouldUseAdminQuery) {
      return adminManualTasksQuery.data?.items ?? []
    }

    return assignedManualTasksQuery.data?.items ?? []
  }, [assignedManualTasksQuery, adminManualTasksQuery, shouldUseAdminQuery])

  return {
    isPending: shouldUseAdminQuery
      ? adminManualTasksQuery.isPending
      : assignedManualTasksQuery.isPending,
    manualTasks
  }
}
